import { useEffect, useState, useRef } from 'react'
import { observer } from 'mobx-react-lite'
import {
  Box, Typography, IconButton, Avatar, Button, Tooltip
} from '@mui/material'
import {
  CallEnd as HangUpIcon,
  Phone as PhoneIcon,
  MicOff as MuteIcon,
  Mic as UnmuteIcon,
  Remove as MinimizeIcon,
  AspectRatio as MaximizeIcon,
  VolumeUp as SpeakerIcon,
  VolumeOff as SpeakerOffIcon,
  PhoneCallback as PhoneIncomingIcon,
  PhoneForwarded as PhoneOutgoingIcon,
} from '@mui/icons-material'
import { callStore } from '../../lib/stores/callStore'
import { maskPhoneNumber } from '../../utils/phoneUtils'

/** Formats elapsed seconds as MM:SS */
function formatTimer(seconds: number): string {
  const m = Math.floor(seconds / 60).toString().padStart(2, '0')
  const s = (seconds % 60).toString().padStart(2, '0')
  return `${m}:${s}`
}

function getInitials(name: string): string {
  return name.split(' ').slice(0, 2).map(n => n[0]?.toUpperCase() ?? '').join('')
}

export const CallingUI = observer(() => {
  const {
    phase,
    target,
    activeCall,
    isMinimized,
    isMuted,
    isSpeakerOn,
    isStarting,
    isEnding,
    elapsedSeconds
  } = callStore

  // Draggable state (initial position aligns to top-right, adjusting for screen width)
  const [position, setPosition] = useState({ 
    x: typeof window !== 'undefined' ? Math.max(20, window.innerWidth - 380) : 1091, 
    y: 76 
  })
  const [dragging, setDragging] = useState(false)
  const [rel, setRel] = useState({ x: 0, y: 0 })

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return // Left click only
    // Don't drag if clicking buttons or interactive elements
    if ((e.target as HTMLElement).closest('button, input, select, .MuiIconButton-root, .MuiButton-root')) return

    setDragging(true)
    setRel({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    })
    e.preventDefault()
  }

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!dragging) return
      // Prevent off-screen dragging slightly
      const x = Math.max(0, Math.min(e.clientX - rel.x, window.innerWidth - (isMinimized ? 430 : 340)))
      const y = Math.max(0, Math.min(e.clientY - rel.y, window.innerHeight - (isMinimized ? 70 : 310)))
      setPosition({ x, y })
    }
    const handleMouseUp = () => {
      setDragging(false)
    }

    if (dragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [dragging, rel, isMinimized])

  if (phase === 'IDLE' || !target) return null

  // Shared Lavender/Purple Gradient Background style
  const cardBgStyle = {
    background: 'linear-gradient(135deg, #e9d5ff 0%, #fdf4ff 100%)',
    borderRadius: '16px',
    boxShadow: '0 10px 30px rgba(76, 29, 149, 0.15), 0 1px 3px rgba(0,0,0,0.05)',
    border: '1px solid rgba(139, 92, 246, 0.2)',
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    left: position.x,
    top: position.y,
    zIndex: 1400,
    cursor: dragging ? 'grabbing' : 'grab',
    userSelect: 'none' as const,
    boxSizing: 'border-box' as const,
  }

  // 1. Minimized Layout (Image 5)
  if (isMinimized) {
    const isIncoming = activeCall?.callType === 'INCOMING'
    const PhoneIconType = isIncoming ? PhoneIncomingIcon : PhoneOutgoingIcon

    return (
      <Box
        onMouseDown={handleMouseDown}
        sx={{
          ...cardBgStyle,
          width: 420,
          height: 60,
          borderRadius: '30px',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2.5,
          py: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <PhoneIconType sx={{ color: '#4c1d95', fontSize: 20 }} />
          <Typography
            noWrap
            sx={{
              fontWeight: 700,
              fontSize: '14px',
              color: '#1e1b4b',
              maxWidth: 160
            }}
          >
            {target.name}
          </Typography>
        </Box>

        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 600,
            color: '#4b5563',
            fontVariantNumeric: 'tabular-nums',
            mx: 2
          }}
        >
          {formatTimer(elapsedSeconds)}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Maximize">
            <IconButton
              size="small"
              onClick={() => callStore.setMinimized(false)}
              sx={{
                color: '#4c1d95',
                bgcolor: 'rgba(255,255,255,0.4)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.6)' }
              }}
            >
              <MaximizeIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          <Tooltip title={isMuted ? 'Unmute' : 'Mute'}>
            <IconButton
              size="small"
              onClick={() => callStore.toggleMute()}
              sx={{
                color: isMuted ? '#ef4444' : '#4c1d95',
                bgcolor: isMuted ? 'rgba(239, 68, 68, 0.15)' : 'rgba(255,255,255,0.4)',
                '&:hover': { bgcolor: isMuted ? 'rgba(239, 68, 68, 0.25)' : 'rgba(255,255,255,0.6)' }
              }}
            >
              {isMuted ? <MuteIcon sx={{ fontSize: 18 }} /> : <UnmuteIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          </Tooltip>

          <Tooltip title="End Call">
            <IconButton
              size="small"
              disabled={isEnding}
              onClick={() => callStore.endActiveCall('CONNECTED')}
              sx={{
                bgcolor: '#dc2626',
                color: '#fff',
                width: 36,
                height: 36,
                boxShadow: '0 4px 10px rgba(220,38,38,0.3)',
                '&:hover': { bgcolor: '#b91c1c' },
                '&:disabled': { bgcolor: '#9ca3af' }
              }}
            >
              <HangUpIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    )
  }

  // Helper variables for layout modes
  const initials = getInitials(target.name)

  // 2. Place Call Layout (Image 1 & 2)
  if (phase === 'PLACE_CALL') {
    return (
      <Box
        onMouseDown={handleMouseDown}
        sx={{
          ...cardBgStyle,
          width: 329,
          height: 298,
          p: 2.5,
          justifyContent: 'space-between'
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PhoneIcon sx={{ color: '#4c1d95', transform: 'rotate(90deg)', fontSize: 18 }} />
          <Typography sx={{ fontWeight: 700, fontSize: '15px', color: '#1e1b4b' }}>
            Place Call
          </Typography>
        </Box>

        {/* Center Info */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1, my: 0.5 }}>
          <Avatar
            sx={{
              width: 56,
              height: 56,
              bgcolor: '#fff',
              color: '#4c1d95',
              fontSize: '18px',
              fontWeight: 700,
              border: '1.5px solid rgba(139, 92, 246, 0.3)',
              boxShadow: '0 4px 12px rgba(139, 92, 246, 0.1)',
            }}
          >
            {initials}
          </Avatar>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '16px', color: '#1e1b4b' }}>
              {target.name}
            </Typography>
            {target.designation || target.companyName ? (
              <Typography sx={{ fontSize: '12px', color: '#4b5563', mt: 0.25 }}>
                {target.designation || ''}{target.designation && target.companyName ? ' · ' : ''}{target.companyName || ''}
              </Typography>
            ) : null}
            <Typography sx={{ fontSize: '12px', color: '#6b7280', mt: 0.25 }}>
              {maskPhoneNumber(target.phone)}
            </Typography>
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center' }}>
          <Button
            variant="outlined"
            onClick={() => callStore.cancelPlaceCall()}
            sx={{
              flex: 1,
              height: 40,
              textTransform: 'none',
              fontWeight: 600,
              borderColor: '#4c1d95',
              color: '#4c1d95',
              borderRadius: '10px',
              '&:hover': {
                borderColor: '#6d28d9',
                bgcolor: 'rgba(76, 29, 149, 0.05)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={isStarting}
            onClick={() => callStore.startOutgoingCall()}
            startIcon={<PhoneIcon sx={{ fontSize: 16 }} />}
            sx={{
              flex: 1,
              height: 40,
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#4c1d95',
              color: '#fff',
              borderRadius: '10px',
              boxShadow: '0 4px 12px rgba(76, 29, 149, 0.2)',
              '&:hover': {
                bgcolor: '#6d28d9',
              }
            }}
          >
            Place Call
          </Button>
        </Box>
      </Box>
    )
  }

  // 3. Outgoing Call / In Call Layout (Image 3)
  if (phase === 'CALLING') {
    const isIncoming = activeCall?.callType === 'INCOMING'
    const headerTitle = isIncoming ? 'Incoming Call' : 'Outgoing Call'
    const PhoneIconType = isIncoming ? PhoneIncomingIcon : PhoneOutgoingIcon

    return (
      <Box
        onMouseDown={handleMouseDown}
        sx={{
          ...cardBgStyle,
          width: 329,
          height: 298,
          p: 2.5,
          justifyContent: 'space-between'
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhoneIconType sx={{ color: '#4c1d95', fontSize: 18 }} />
            <Typography sx={{ fontWeight: 700, fontSize: '15px', color: '#1e1b4b' }}>
              {headerTitle}
            </Typography>
          </Box>
          <Tooltip title="Minimize">
            <IconButton
              size="small"
              onClick={() => callStore.setMinimized(true)}
              sx={{
                color: '#4c1d95',
                bgcolor: 'rgba(255,255,255,0.4)',
                width: 28,
                height: 28,
                borderRadius: '8px',
                border: '1px solid rgba(139, 92, 246, 0.2)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.6)' }
              }}
            >
              <MinimizeIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Center Info */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 0.75 }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#4b5563' }}>
            In Progress
          </Typography>

          <Avatar
            sx={{
              width: 56,
              height: 56,
              bgcolor: '#fff',
              color: '#4c1d95',
              fontSize: '18px',
              fontWeight: 700,
              border: '1.5px solid rgba(139, 92, 246, 0.3)',
              boxShadow: '0 4px 12px rgba(139, 92, 246, 0.1)',
              my: 0.5
            }}
          >
            {initials}
          </Avatar>

          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '16px', color: '#1e1b4b' }}>
              {target.name}
            </Typography>
            <Typography sx={{ fontSize: '12px', color: '#6b7280', mt: 0.25 }}>
              {maskPhoneNumber(target.phone)}
            </Typography>
          </Box>
        </Box>

        {/* Empty spacer to preserve card height */}
        <Box />
      </Box>
    )
  }

  // 4. Incoming Call Layout (Image 4)
  if (phase === 'INCOMING') {
    return (
      <Box
        onMouseDown={handleMouseDown}
        sx={{
          ...cardBgStyle,
          width: 329,
          height: 298,
          p: 2.5,
          justifyContent: 'space-between'
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhoneIncomingIcon sx={{ color: '#4c1d95', fontSize: 18 }} />
            <Typography sx={{ fontWeight: 700, fontSize: '15px', color: '#1e1b4b' }}>
              Incoming Call
            </Typography>
          </Box>
          <Tooltip title="Minimize">
            <IconButton
              size="small"
              onClick={() => callStore.setMinimized(true)}
              sx={{
                color: '#4c1d95',
                bgcolor: 'rgba(255,255,255,0.4)',
                width: 28,
                height: 28,
                borderRadius: '8px',
                border: '1px solid rgba(139, 92, 246, 0.2)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.6)' }
              }}
            >
              <MinimizeIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Center Info */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 0.75, my: 0.5 }}>
          <Avatar
            sx={{
              width: 56,
              height: 56,
              bgcolor: '#fff',
              color: '#4c1d95',
              fontSize: '18px',
              fontWeight: 700,
              border: '1.5px solid rgba(139, 92, 246, 0.3)',
              boxShadow: '0 4px 12px rgba(139, 92, 246, 0.1)',
              mb: 0.5
            }}
          >
            {initials}
          </Avatar>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '16px', color: '#1e1b4b' }}>
              {target.name}
            </Typography>
            <Typography sx={{ fontSize: '12px', color: '#6b7280', mt: 0.25 }}>
              {maskPhoneNumber(target.phone)}
            </Typography>
            <Typography sx={{ fontSize: '12px', color: '#4b5563', mt: 0.5, fontStyle: 'italic' }}>
              Is calling you
            </Typography>
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center' }}>
          <Button
            variant="contained"
            onClick={() => callStore.rejectIncomingCall()}
            startIcon={<HangUpIcon sx={{ fontSize: 16 }} />}
            sx={{
              flex: 1,
              height: 40,
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#b91c1c',
              color: '#fff',
              borderRadius: '10px',
              boxShadow: '0 4px 12px rgba(185, 28, 28, 0.2)',
              '&:hover': {
                bgcolor: '#991b1b',
              }
            }}
          >
            Reject
          </Button>
          <Button
            variant="contained"
            onClick={() => callStore.acceptIncomingCall()}
            startIcon={<PhoneIcon sx={{ fontSize: 16 }} />}
            sx={{
              flex: 1,
              height: 40,
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#16a34a',
              color: '#fff',
              borderRadius: '10px',
              boxShadow: '0 4px 12px rgba(22, 163, 74, 0.2)',
              '&:hover': {
                bgcolor: '#15803d',
              }
            }}
          >
            Accept
          </Button>
        </Box>
      </Box>
    )
  }

  return null
})
